import React, { useState, useEffect, useContext } from 'react'

import ThemeSwitcher from 'react-css-vars'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Button } from 'semantic-ui-react'

import { IProject, IClip, ITheme } from '../../../api/Data'
import Data from '../../../api/Data'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { UserContext } from '../../../contexts/UserContext'
import { flattenClipsList } from '../../../utils/sharedFunctions'

import SubSection from '../../../components/common/SubSection'
import Title from '../../../components/common/Title'

import ConfigureTabOverviewPage from './ConfigureTabOverviewPage'

var tinycolor = require('tinycolor2')

const CollectTab = (props: IProps) => {
  const { hasUserMadeChange, dispatchGlobalContext } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)

  // Props
  const { projectTitle, history, location, match, project, theme, setTheme, setProject, exitProject } = props

  const { hasBrandedUploadPage } = userProfile.plan

  // States
  const [activeTab, setActiveTab] = useState<string>('Overview')
  const [clips, setClips] = useState<IClip[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)

  // Url search query navigation
  useEffect(() => {
    if (location.search.startsWith('?tab=overview')) setActiveTab('Overview')
    else history.push(`/projects/${match.params.projectSlug}/configure?tab=overview`)
  }, [history, location.search, match.params.projectSlug])
  
  useEffect(() => {
    const fetchClips = async () => {
      const slug = match.params.projectSlug as string

      const { data: clipData, error } = await Data.getClips({
        projectSlug: slug,
        limit: 1000,
        offset: 0,
        searchPhrase: '',
        tags: [],
        orderBy: 'createdAt DESC'
      })

      if (error) return alert('An error occurred')

      setClips(flattenClipsList(clipData.rows))
    }

    fetchClips()
  }, [])

  // ------------------------------------------------Project handles------------------------------------------------
  const handleProjectChange = (e: any, data: any) => {
    const { name, value, checked, values } = data

    // Record any change made on the form
    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    // Reset errors
    if (validationError === name) setValidationError(null)
    
    let newValue
    
    if (['allowRecordVideo', 'allowUploadFile', 'allowQrCode', 'shouldEmailEverySubmission'].includes(name))
      newValue = checked
    // If value is an array
    else if (name === 'questions')
      newValue = values
    // If no deadline, set it to null
    else if (name === 'deadline' && value === '')
      newValue = null
    // Else set new value
    else
      newValue = value
    
    setProject({ ...project, [name]: newValue })
  }

  const handleChangePrimaryFont = (newFont: { family: string }) => {
    if (!hasBrandedUploadPage) return

    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    const newFontFamily = newFont.family

    setProject({ ...project, ...{ displaySetting: { ...project.displaySetting, ...{ primaryFont: newFontFamily } } } })
  }

  const handleChangePrimaryColor = (newColor: { hex: string }) => {
    if (!hasBrandedUploadPage) return

    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    const hexColor = newColor.hex

    setProject({
      ...project,
      ...{
        displaySetting: {
          ...project.displaySetting,
          ...{
            primaryColor: hexColor,
            buttonTextColor: tinycolor(hexColor).getBrightness() >= 200 ? '#000000' : '#FFFFFF',
          },
        },
      },
    })
  }

  const handleSubmitProject = async () => {
    dispatchGlobalContext({ type: 'set loading', payload: true })

    const { data, error } = await Data.updateProject(project)

    if (error) {
      dispatchGlobalContext({ type: 'set loading', payload: false })
      alert('Oops, something went wrong')
    }

    setTimeout(() => {
      setProject(data)
      dispatchGlobalContext({ type: 'set loading', payload: false })
    }, 1000)
  }

  // ------------------------------------------------Theme handles------------------------------------------------
  const handleSubmitTheme = async () => {
    dispatchGlobalContext({ type: 'set loading', payload: true })

    const { data, error } = await Data.updateTheme(theme)

    dispatchGlobalContext({ type: 'set user has made changes', payload: false })

    if (error) {
      dispatchGlobalContext({ type: 'set loading', payload: false })

      dispatchGlobalContext({ type: 'set user has made changes', payload: false })

      return alert(`An error occurred while updating your theme => ${error.response?.data}`)
    }

    setTheme(data)

    setTimeout(() => dispatchGlobalContext({ type: 'set loading', payload: false }), 1000)
  }

  // TODO: here we save both the project and the theme every time the user clicks "save". We should separate that.
  const hasFormError = () => {
    const { name, title } = project

    if (!title) {
      setValidationError('title')
      history.push(`/projects/${match.params.projectSlug}/configure?tab=overview`)
      return true
    }

    if (!name) {
      setValidationError('name')
      history.push(`/projects/${match.params.projectSlug}/configure?tab=overview`)
      return true
    }
  }
  
  const handleSave = () => {
    if (hasFormError()) return
    handleSubmitProject()
    handleSubmitTheme()
  }
  
  const { displaySetting } = project
  const { primaryFont, primaryColor, buttonTextColor } = displaySetting

  const uploadPageTheme = {
    primaryFont: hasBrandedUploadPage ? primaryFont : 'Roboto',
    primaryColor: hasBrandedUploadPage ? primaryColor : '#6b50ff',
    buttonTextColor: hasBrandedUploadPage ? buttonTextColor : '#FFFFFF',
  }
  console.log('Rendering Collect tab Container', activeTab, clips)

  return (
    <ThemeSwitcher theme={uploadPageTheme}>
      {/* title */}
      <Title title={projectTitle}>
        <>
          <Button compact secondary disabled={!hasUserMadeChange} onClick={handleSave} style={{ border: '1px solid #fff' }}>
            Save
          </Button>
          <Button compact onClick={exitProject}>
            Close
          </Button>
        </>
      </Title>

      <SubSection hasGreyBackground>
        <>
          {activeTab === 'Overview' && (
            <ConfigureTabOverviewPage
              project={project}
              clips={clips}
              handleChange={handleProjectChange}
              handleChangePrimaryFont={handleChangePrimaryFont}
              handleChangePrimaryColor={handleChangePrimaryColor}
              validationError={validationError}
            />
          )}
        </>
      </SubSection>
    </ThemeSwitcher>
  )
}

interface IProps extends RouteComponentProps<any> {
  projectTitle: string
  project: IProject
  theme: ITheme
  setProject: (arg: IProject) => void
  setTheme: (arg: ITheme) => void
  exitProject: () => void
}

export default withRouter(CollectTab)
